import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export interface AudioPlayerRequest {
  /**
   * Already-fetched media. Recordings sit behind authenticated proxy endpoints,
   * so callers fetch the blob themselves (keeping their own button spinner and
   * error message) and hand it over here.
   */
  blob: Blob;
  title: string;
  subtitle?: string;
  kind?: "audio" | "video";
}

export interface AudioPlayerTrack {
  url: string;
  title: string;
  subtitle?: string;
  kind: "audio" | "video";
  /** Bumped on every play() so replaying the same recording still starts a fresh element. */
  token: number;
}

interface AudioPlayerActions {
  play: (request: AudioPlayerRequest) => void;
  close: () => void;
}

// Actions and track live in separate contexts: pages that start playback only
// need the (stable) actions, so they must not re-render each time a recording
// starts or is closed.
const AudioPlayerActionsContext = createContext<AudioPlayerActions>({
  play: () => {},
  close: () => {},
});
const AudioPlayerTrackContext = createContext<AudioPlayerTrack | null>(null);

/** Start or close playback in the site-wide player widget. */
export const useAudioPlayer = () => useContext(AudioPlayerActionsContext);

/** The current track — for the widget itself. */
export const useAudioPlayerTrack = () => useContext(AudioPlayerTrackContext);

/**
 * Owns the single active recording and its object URL. Mounted at the layout
 * level (app.tsx childrenRender) together with <AudioPlayerWidget />, so
 * playback survives page navigation and is torn down on logout.
 */
export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [track, setTrack] = useState<AudioPlayerTrack | null>(null);
  const urlRef = useRef<string | null>(null);
  const tokenRef = useRef(0);

  const revokeCurrent = useCallback(() => {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = null;
  }, []);

  const play = useCallback(
    ({ blob, title, subtitle, kind = "audio" }: AudioPlayerRequest) => {
      revokeCurrent();
      const url = URL.createObjectURL(blob);
      urlRef.current = url;
      tokenRef.current += 1;
      setTrack({ url, title, subtitle, kind, token: tokenRef.current });
    },
    [revokeCurrent],
  );

  const close = useCallback(() => {
    revokeCurrent();
    setTrack(null);
  }, [revokeCurrent]);

  useEffect(() => revokeCurrent, [revokeCurrent]);

  const actions = useMemo(() => ({ play, close }), [play, close]);

  return (
    <AudioPlayerActionsContext.Provider value={actions}>
      <AudioPlayerTrackContext.Provider value={track}>{children}</AudioPlayerTrackContext.Provider>
    </AudioPlayerActionsContext.Provider>
  );
};
